import { useEffect, useRef, useState } from 'react';
import './ContactUs.scss';
import { getLanguage } from '../../utils/Helper';
import SocialLinks from '../../components/general/social-links/SocialLinks';

const ContactUs = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionsRef = useRef([]);
  const lang = getLanguage();

  const content = {
    EN: {
      hero: {
        label: "Get In Touch",
        title: "Contact Us",
        tagline: "We're Here to Help 😊",
        description: "If you have any questions, requests, or concerns — don't hesitate to reach out. We're always happy to hear from you!",
      },
      info: {
        title: "Reach Us Directly",
        items: [
          {
            icon: "📱",
            title: "Phone / WhatsApp",
            value: "+201006611664",
            link: "https://wa.me/201006611664",
            linkLabel: "Chat on WhatsApp",
          },
          {
            icon: "✉️",
            title: "Email",
            value: "addmixstore@gmail.com",
            link: "mailto:addmixstore@gmail.com",
            linkLabel: "Send an Email",
          },
          {
            icon: "📍",
            title: "Address",
            value: "Al Gamea Al Haditha El Mokattam, Cairo, Egypt",
            link: null,
          },
        ],
      },
      social: {
        title: "Follow Us & Stay Connected",
        subtitle: "Follow us on social media and reach out anytime!",
      },
      map: {
        title: "Find Us on the Map",
      },
    },
    AR: {
      hero: {
        label: "تواصل معنا",
        title: "اتصل بنا",
        tagline: "إحنا هنا نساعدك 😊",
        description: "لو عندك أي أسئلة أو طلبات أو مخاوف — متترددش تتواصل معانا. دايماً بنفرح لما نسمع منك!",
      },
      info: {
        title: "تواصل معانا مباشرة",
        items: [
          {
            icon: "📱",
            title: "تليفون / واتساب",
            value: "+201006611664",
            link: "https://wa.me/201006611664",
            linkLabel: "ابدأ محادثة على واتساب",
          },
          {
            icon: "✉️",
            title: "إيميل",
            value: "addmixstore@gmail.com",
            link: "mailto:addmixstore@gmail.com",
            linkLabel: "ابعت إيميل",
          },
          {
            icon: "📍",
            title: "العنوان",
            value: "المقطم الهضبة الوسطي, ش الجامعة الحديثة بجوار صيدلية شاهبور",
            link: null,
          },
        ],
      },
      social: {
        title: "تابعنا وابقى على تواصل",
        subtitle: "تابعنا على السوشيال ميديا وتواصل معانا في أي وقت!",
      },
      map: {
        title: "اعرف مكاننا على الخريطة",
      },
    },
  };

  const c = content[lang] || content.EN;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.dataset.section]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    sectionsRef.current.forEach((s) => s && observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) sectionsRef.current.push(el);
  };

  return (
    <div className={`contact-page ${lang === 'AR' ? 'rtl' : 'ltr'}`}>

      {/* ── Hero ── */}
      <section className="contact-hero-section">
        <div className="contact-hero-background">
          <div className="contact-orb orb-1" />
          <div className="contact-orb orb-2" />
          <div className="contact-orb orb-3" />
          <div className="hero-emoji-bg">💬</div>
        </div>

        <div className="contact-hero-content">
          <div className="contact-hero-text">
            <span className="contact-label">{c.hero.label}</span>
            <h1 className="contact-title">{c.hero.title}</h1>
            <p className="contact-tagline">{c.hero.tagline}</p>
            <p className="contact-description">{c.hero.description}</p>
          </div>
        </div>

        <div className="scroll-indicator">
          <div className="mouse"><div className="wheel" /></div>
        </div>
      </section>


      {/* ── Map ── */}
      <section
        className={`contact-map-section ${isVisible.map ? 'visible' : ''}`}
        data-section="map"
        ref={addToRefs}
      >
        <h2 className="contact-section-title">{c.map.title}</h2>
        <div className="map-wrapper">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d863.8971952203083!2d31.317303269627487!3d29.99124585996777!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1458398040fdcfef%3A0x72176d24aba06ea3!2sAddmix%20Store!5e0!3m2!1sen!2seg!4v1770815254405!5m2!1sen!2seg"
            width="100%"
            height="400"
            style={{ border: '0px' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Addmix Store Location"
          />
        </div>
      </section>

      {/* ── Contact Info Cards ── */}
      <section
        className={`contact-info-section ${isVisible.info ? 'visible' : ''}`}
        data-section="info"
        ref={addToRefs}
      >
        <h2 className="contact-section-title">{c.info.title}</h2>
        <div className="contact-info-grid">
          {c.info.items.map((item, i) => (
            <div
              key={i}
              className="contact-info-card"
              style={{ animationDelay: `${i * 0.15}s` }}
            >
              <div className="info-icon">{item.icon}</div>
              <h4>{item.title}</h4>
              <p>{item.value}</p>
              {item.link && (
                <a href={item.link} target="_blank" rel="noreferrer" className="info-link">
                  {item.linkLabel} →
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Social Links ── */}
      <section
        className={`contact-social-section ${isVisible.social ? 'visible' : ''}`}
        data-section="social"
        ref={addToRefs}
      >
        <div className="social-card">
          <div className="social-card-inner">
            <h2>{c.social.title}</h2>
            <p>{c.social.subtitle}</p>
            <SocialLinks />
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactUs;